import { Pencil } from 'lucide-react';
import { jsonPathToFieldName } from '../../components/schemaFieldRemoval';
import type { LookupSchemaSetup } from './lookup-tool-model';
import styles from './LookupTool.module.css';

interface LookupSchemaSummaryProps {
  setup: LookupSchemaSetup;
  onEdit: () => void;
}

export const LookupSchemaSummary = ({ setup, onEdit }: LookupSchemaSummaryProps) => {
  const mappingRows = [
    {
      label: 'Record ID',
      field: setup.recordIdField,
      description: `Identifies each ${setup.name} record.`,
    },
    {
      label: 'Contact ID',
      field: setup.userIdField,
      description: 'Limits results to the contact using the tool.',
    },
    {
      label: 'Record date',
      field: setup.dateField,
      description: `Provides the primary date for each ${setup.name} record.`,
    },
  ];
  const introduction = `When this tool runs, it searches ${setup.name} records linked to the current contact.`;

  return (
    <section className={styles.schemaSummary}>
      <header>
        <div>
          <h3>How this schema is used</h3>
          <p>{introduction}</p>
        </div>
        <button type="button" onClick={onEdit}>
          <Pencil />
          Edit schema setup
        </button>
      </header>
      <section className={styles.mappingSummary}>
        <h4>Record mapping</h4>
        <p>These fields identify each record, link it to the current contact, and provide its date.</p>
        <dl>
          {mappingRows.map(row => (
            <div key={row.label}>
              <dt>{row.label}</dt>
              <dd>
                <code>{row.field || 'Not configured'}</code>
                <span>{row.description}</span>
              </dd>
            </div>
          ))}
        </dl>
      </section>
      <section className={styles.returnedFields}>
        <h4>Fields returned by this tool</h4>
        <p>The tool may return these fields when it finds a matching record.</p>
        <dl>
          {setup.returnedFields.map(field => (
            <div key={`${field.jsonPath}-${field.name}`}>
              <dt>{field.name}</dt>
              <dd>
                From schema field <code>{jsonPathToFieldName(field.jsonPath)}</code>
              </dd>
            </div>
          ))}
        </dl>
      </section>
    </section>
  );
};
